import { computed, ref, Ref } from 'vue'
import { User } from '@/model/data-object/User'
import useAppState, { ViewType } from '@/composables/ui/ui'
import useConfiguration from '@/composables/configuration/configuration'
import useUsersApi from '@/composables/user/userApi'
import { UserUpdate } from '@/model/data-object/UserUpdate'
import useTopic from '@/composables/topic/topic'
import { JwtToken } from '@/model/data-object/JwtToken'
import useUserBlock from '@/composables/userBlock/userBlock'
import useUserFactory from '@/composables/user/userFactory'

const currentUser: Ref<User | undefined> = ref<User>()
const profile = ref<User>(new User())
const jwt: Ref<JwtToken | undefined> = ref<JwtToken>()

const userEnsured: Ref<boolean> = ref<boolean>(false)

export default function useCurrentUser() {
  const { loggingIn, loggedIn } = useAppState()
  const { configuration, getJwt } = useConfiguration()
  const { fetchCurrent: fetchCurrentApi } = useUsersApi()
  const { topic } = useTopic()
  const { response, loading, error, request } = fetchCurrentApi()
  const { fetchBlockedUsers } = useUserBlock()
  const { createFromObject } = useUserFactory()

  const isLoggedIn = computed(() => {
    return typeof jwt.value !== 'undefined'
  })

  async function fetchCurrent() {
    if (!isLoggedIn.value) {
      return
    }

    loggingIn.value = true
    loggedIn.value = false
    await request()
    loggingIn.value = false
    if (response.value === null) {
      return
    }

    currentUser.value = createFromObject(response.value as User)
    await fetchBlockedUsers(currentUser.value)
    loggedIn.value = true
  }

  const isSubscriber = computed(() => {
    let subscriber = false
    if (currentUser.value?.isPayingCustomer) {
      return true
    }
    if (jwt.value && jwt.value?.isSubscriber()) {
      subscriber = true
    }

    return subscriber
  })

  const allowPayingCustomerAction = computed(() => {
    if (currentUser.value) {
      let owner = false
      if (topic.value) {
        owner = topic.value.owners.includes(currentUser.value.id ?? 0)
      }
      return (
        currentUser.value.isPayingCustomer ||
        owner ||
        !configuration.playingCustomerRestriction
      )
    }

    return isSubscriber.value
  })

  const shouldFetchUser = () => {
    return true
    // const { viewType } = useAppState()
    //
    // if (
    //   viewType.value === ViewType.TOP_NEWEST &&
    //   !configuration.widget.showProfileCrossroads
    // ) {
    //   return false
    // }
    //
    // return true
  }

  const ensureCurrentUser = async () => {
    if (typeof jwt.value === 'undefined') {
      jwt.value = getJwt()
      if (typeof jwt.value !== 'undefined') {
        loggedIn.value = true
      }
    }
    if (typeof currentUser.value === 'undefined' && shouldFetchUser()) {
      await fetchCurrent()
    }

    userEnsured.value = true

    return currentUser
  }

  const getUserId = (): number => {
    return jwt.value?.sub ?? 0
  }

  return {
    userEnsured,
    currentUser,
    loggedIn,
    loading,
    error,
    allowPayingCustomerAction,
    isLoggedIn,
    isSubscriber,
    ensureCurrentUser,
    fetchCurrent,
    getUserId,
  }
}

export function updateCurrentUser() {
  const updatingCurrent = ref(false)

  const updateCurrent = async () => {
    const { currentUser } = useCurrentUser()
    const { updateUser } = useUsersApi()

    if (currentUser.value) {
      const { request } = updateUser(
        new UserUpdate(
          currentUser.value.isHideSubscribedUsers,
          currentUser.value.isHideFollowers
        ),
        currentUser.value?.id
      )
      updatingCurrent.value = true
      await request()
      updatingCurrent.value = false
    }
  }

  return {
    updatingCurrent,
    updateCurrent,
  }
}

export function useProfileShow() {
  const { configuration } = useConfiguration()
  const { profileLoading } = useAppState()
  const { fetchSpecific } = useUsersApi()
  const { createFromObject } = useUserFactory()

  const isShowingCurrentUser: Ref<boolean> = computed(
    () =>
      jwt.value?.sub === configuration.widget.userId ||
      currentUser.value?.id === configuration.widget.userId
  )

  const loadProfile = async () => {
    if (isShowingCurrentUser.value && currentUser.value) {
      profile.value = currentUser.value

      return
    }

    const { request, response } = fetchSpecific(configuration.widget.userId)

    profileLoading.value = true
    await request()
    profileLoading.value = false

    if (response.value) {
      profile.value = createFromObject(response.value as User)
    }
  }

  return {
    profile,
    loadProfile,
    isShowingCurrentUser,
  }
}
